import { useQueryClient } from '@tanstack/react-query';
import { FileText, Image as ImageIcon, Info, Lock, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components';
import { toApiError, useApi, type TrashedFileResponse } from '@/shared/api';
import { formatDate } from '@/shared/lib/format';
import { akteErrorMessage } from './akteErrorMessages';
import { folderDisplayPath } from './akteFolderNames';
import { purgeCountdown } from './akteTrashRules';

interface Props {
  propertyId: string;
  items: TrashedFileResponse[];
  loading: boolean;
}

/** Papierkorb-Ansicht: gelöschte Dateien mit Herkunft + Wiederherstellen (kein hartes Löschen im UI). */
export function AkteTrashView({ propertyId, items, loading }: Props) {
  const api = useApi();
  const qc = useQueryClient();

  function restore(item: TrashedFileResponse) {
    api.propertyFiles
      .restore(propertyId, item.id)
      .then((res) => {
        toast.success(`Wiederhergestellt in „${folderDisplayPath(res.folder)}".`);
        void qc.invalidateQueries({ queryKey: ['propertyFiles'] });
      })
      .catch(async (err: unknown) => toast.error(akteErrorMessage(await toApiError(err))));
  }

  return (
    <section className="border-line bg-card/40 rounded-lg border p-4 sm:p-5">
      <header className="mb-3 flex items-center gap-2">
        <Trash2 size={18} className="text-ink-soft shrink-0" />
        <h2 className="text-ink font-serif text-lg">Papierkorb</h2>
        {items.length > 0 && (
          <span className="bg-card text-ink-soft rounded px-1.5 py-0.5 text-xs tabular-nums">
            {items.length}
          </span>
        )}
      </header>

      <p className="text-ink-faint mb-4 flex items-start gap-2 text-xs">
        <Info size={14} className="mt-0.5 shrink-0" />
        Gelöschte Dokumente bleiben 30 Tage hier und werden danach automatisch entfernt. Gesperrte
        Belege bleiben dauerhaft erhalten.
      </p>

      {loading ? (
        <p className="text-ink-faint py-8 text-center text-sm">Lade Papierkorb…</p>
      ) : items.length === 0 ? (
        <p className="text-ink-soft py-10 text-center text-sm">
          Der Papierkorb ist leer — hier liegt nichts, was Sie zurückholen könnten.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <TrashRow key={item.id} item={item} onRestore={() => restore(item)} />
          ))}
        </ul>
      )}
    </section>
  );
}

function TrashRow({ item, onRestore }: { item: TrashedFileResponse; onRestore: () => void }) {
  const countdown = purgeCountdown(item);
  return (
    <li className="border-line bg-card flex flex-col gap-2 rounded-lg border px-3 py-2.5 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="bg-card-alt text-ink-soft flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          {item.contentType.startsWith('image/') ? <ImageIcon size={18} /> : <FileText size={18} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-ink truncate font-medium" title={item.originalFileName ?? undefined}>
            {item.originalFileName ?? 'Ohne Dateinamen'}
          </p>
          <p className="text-ink-faint truncate text-xs" title={item.originalFolder ?? undefined}>
            {[
              item.originalFolder ? `aus ${folderDisplayPath(item.originalFolder)}` : null,
              item.deletedAt ? `gelöscht am ${formatDate(item.deletedAt)}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {/* Statusfarbe nie ohne Text (§11): der Countdown sagt, wie eilig es ist. */}
          <p
            className={`mt-0.5 flex items-center gap-1 text-xs font-medium ${countdown.tone === 'urgent' ? 'text-sealwax' : countdown.tone === 'kept' ? 'text-ink-soft' : 'text-ink-faint'}`}
          >
            {countdown.tone === 'kept' && <Lock size={12} className="shrink-0" />}
            {countdown.label}
          </p>
        </div>
      </div>
      <Button
        variant="ghost"
        leftIcon={<RotateCcw size={14} />}
        onClick={onRestore}
        className="w-full shrink-0 sm:w-auto"
      >
        Wiederherstellen
      </Button>
    </li>
  );
}
